import Grid from '@mui/material/Grid2';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { RefObject, useState } from 'react';
import { toast } from 'material-react-toastify';
import { useRecoilValue } from 'recoil';
import WhatsAppIcon from '@mui/icons-material/WhatsApp'; // place at top if not already
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { sharedStyles } from '../../../utils/Style';
import { OrderDetailsRow } from './OrderDetailsRow';
import { formatLocalTime } from '../../../utils/Date';
import { OrderStatusDropdown } from '../../../components/Dropdowns/OrderStatusDropdown';
import { getCargoStatusName } from '../../../utils/CargoStatus';
import { OrderProductsTable } from './OrderProductsTable';
import { PayBreakdown } from './PayBreakdown';
import { Order } from '../../../interfaces/Order/Order';
import {
  OrderStatus,
  orderStatusNameMap,
} from '../../../interfaces/Order/OrderStatus';
import { updateOrderStatus } from '../../../api/Order';
import { shippingProvidersAtom } from '../../../atoms/ShippingProvider';
import { OrderMessageDialog } from './OrderMessageDialog';

type OrderDetailsPaperProps = {
  order: Order;
  paperRef?: RefObject<HTMLDivElement>;
  onUpdate?: () => void;
  width?: number;
  breakPage?: boolean;
};

export const OrderDetailsPaper = ({
  order,
  paperRef,
  onUpdate,
  width,
  breakPage = false,
}: OrderDetailsPaperProps) => {
  const shippingProviders = useRecoilValue(shippingProvidersAtom);

  const [loadingStatus, setLoadingStatus] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);

  const handleChangeStatus = (newStatus: OrderStatus) => {
    if (!order) {
      return;
    }

    setLoadingStatus(true);
    updateOrderStatus([order.id], newStatus)
      .then(() => {
        toast.success('Sipariş durumu başarıyla güncellendi');
        onUpdate?.();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingStatus(false));
  };

  const handleCopyNotes = () => {
    navigator.clipboard
      .writeText(order.notes)
      .then(() => toast.success('Sipariş notu panoya kopyalandı'));
  };

  const shippingProviderName =
    shippingProviders.find((s) => s.id.toString() === order.shippingMethod)
      ?.name ?? order.shippingMethod;

  return (
    <Paper
      ref={paperRef}
      sx={{
        p: 3,
        width,
        pageBreakAfter: breakPage ? 'always' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <OrderMessageDialog
        orderID={order.id}
        firstName={order.customerFirstName}
        lastName={order.customerLastName}
        phoneNumber={order.customerPhone}
        open={showMessageDialog}
        onClose={() => setShowMessageDialog(false)}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
              Sipariş Bilgisi
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <OrderDetailsRow
              title='Sipariş Tarihi'
              value={formatLocalTime(order.createdAt)}
            />

            <OrderDetailsRow
              title='Sipariş Durumu'
              value={
                onUpdate ? (
                  <OrderStatusDropdown
                    value={order.status}
                    onChange={(newStatus) =>
                      handleChangeStatus(newStatus ?? order.status)
                    }
                    required
                    loading={loadingStatus}
                  />
                ) : (
                  orderStatusNameMap[order.status]
                )
              }
            />

            <OrderDetailsRow title='Kargo Tipi' value={shippingProviderName} />

            <OrderDetailsRow title='Kargo Numarası' value={order.cargoNo} />
            <OrderDetailsRow title='Kargo Anahtarı' value={order.cargoKey} />
            <OrderDetailsRow
              title='Kargo Durumu'
              value={getCargoStatusName(order.cargoStatus)}
            />

            <OrderDetailsRow
              title='Ödeme Yöntemi'
              value={order.paymentMethod}
            />

            {!!order.affiliateCode && (
              <OrderDetailsRow
                title='Affiliate Kodu'
                value={order.affiliateCode}
              />
            )}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
              Hesap Bilgisi
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <OrderDetailsRow
              title='Müşteri İsmi'
              value={`${order.customerFirstName} ${order.customerLastName}`}
            />

            <OrderDetailsRow
              title='Müşteri Email'
              value={order.customerEmail}
            />

            <OrderDetailsRow
              title='Müşteri Telefon'
              value={order.customerPhone}
              rightSection={
                <IconButton
                  color='success'
                  onClick={() => setShowMessageDialog(true)}
                  className='no-print'
                  sx={{ p: 0 }}
                >
                  <WhatsAppIcon fontSize='small' />
                </IconButton>
              }
            />

            <OrderDetailsRow title='Vergi Numarası' value={order.taxNumber} />

            <OrderDetailsRow title='Vergi Dairesi' value={order.taxOffice} />

            <OrderDetailsRow
              title='Müşteri Grubu'
              value={order.customerCompany ?? 'Misafir'}
            />
          </Box>
        </Grid>

        <Grid size={12} />

        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
              Ödeme Adresi
            </Typography>
            <Divider sx={{ mt: 1, mb: 1.5 }} />

            <Typography sx={{ ...sharedStyles.body1 }}>
              {`${order.billingFirstName} ${order.billingLastName}`}
            </Typography>

            <Typography sx={{ ...sharedStyles.body1 }}>
              {order.billingAddress1}
            </Typography>

            {!!order.billingAddress2 && (
              <Typography sx={{ ...sharedStyles.body1 }}>
                {order.billingAddress2}
              </Typography>
            )}

            <Typography sx={{ ...sharedStyles.body1 }}>
              {`${order.billingCity}, ${order.billingState} ${
                order.billingZip ?? ''
              }`}
            </Typography>

            <Typography sx={{ ...sharedStyles.body1 }}>
              {order.billingCountry}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
              Kargo Adresi
            </Typography>
            <Divider sx={{ mt: 1, mb: 1.5 }} />

            <Typography sx={{ ...sharedStyles.body1 }}>
              {`${order.shippingFirstName} ${order.shippingLastName}`}
            </Typography>

            <Typography sx={{ ...sharedStyles.body1 }}>
              {order.shippingAddress1}
            </Typography>

            {!!order.shippingAddress2 && (
              <Typography sx={{ ...sharedStyles.body1 }}>
                {order.shippingAddress2}
              </Typography>
            )}

            <Typography sx={{ ...sharedStyles.body1 }}>
              {`${order.shippingCity}, ${order.shippingState} ${
                order.shippingZip ?? ''
              }`}
            </Typography>

            <Typography sx={{ ...sharedStyles.body1 }}>
              {order.shippingCountry}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {!!order.notes && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
              alignItems: 'center',
              mt: 3,
              mb: 0.5,
            }}
          >
            <Typography sx={{ ...sharedStyles.h5, fontWeight: 'bold' }}>
              Sipariş Notu
            </Typography>

            <IconButton
              size='small'
              color='primary'
              onClick={handleCopyNotes}
              className='no-print'
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>

          <TextField
            multiline
            fullWidth
            disabled
            minRows={3}
            value={order.notes}
          />
        </>
      )}

      <Typography
        sx={{ ...sharedStyles.h5, fontWeight: 'bold', mt: 3, mb: 1.5 }}
      >
        Sipariş İçeriği
      </Typography>

      <OrderProductsTable orderProducts={order.products} />

      <Box sx={{ mt: 3 }} />

      <PayBreakdown order={order} shippingProviderName={shippingProviderName} />
    </Paper>
  );
};
